import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { OrderSchemaType, ItemOrderSchemaType } from '../order.schema';

export const useOrderCalculations = () => {
  const { watch, setValue, getValues } = useFormContext<OrderSchemaType>();

  const items = watch('items');
  const shippingValue = watch('shippingValue') || 0;

  const calculateItemData = (item: ItemOrderSchemaType, isRoot = false): { quantity: number; taxes: number; subtotal: number } => {
    let quantity = 0;
    let taxes = 0;
    let subtotal = 0;

    if (item.isSaved) {
      if (item.itemType === 'MATERIAL') {
        quantity += Number(item.feedstock?.quantidade) || 0;
      } else if (item.itemType === 'PRODUCT') {
        quantity += Number(item.finalProduct?.quantidade) || 0;
      } else if (item.itemType === 'RESOURCE') {
        quantity += Number(item.resource?.quantidade) || 0;
      } else if (item.itemType === 'SERVICE') {
        quantity += Number(item.stage?.quantidade) || 0;
      }

      taxes += Number(item.priceDetails?.itemTaxes || 0);
      // Achado 10 — pedido salvo antes do calculator montar: cai pro itemPrice persistido.
      // Só no item raiz: o itemPrice do pai já engloba os subitens (duplicaria neles).
      subtotal += Number(item.priceDetails?.itemSubtotal) || (isRoot ? Number((item as any).itemPrice) || 0 : 0);
    }

    if (item.subItems && Array.isArray(item.subItems)) {
      for (const subItem of item.subItems) {
        const subItemData = calculateItemData(subItem);
        quantity += subItemData.quantity;
        taxes += subItemData.taxes;
        // The parent's itemSubtotal already includes SERVICE subtotals via useItemSubtotalCalculator
        if (subItem.itemType !== 'SERVICE') {
          subtotal += subItemData.subtotal;
        }
      }
    }

    return { quantity, taxes, subtotal };
  };

  const totals = useMemo(() => {
    let totalQuantity = 0;
    let totalTaxes = 0;
    let grandTotal = 0;
    let practicedTotal = 0;

    if (Array.isArray(items)) {
      for (const item of items) {
        const itemData = calculateItemData(item, true);
        totalQuantity += itemData.quantity;
        totalTaxes += itemData.taxes;
        grandTotal += itemData.subtotal;
        const sp = Number((item as any)?.priceDetails?.sellingPrice);
        practicedTotal += !isNaN(sp) && sp > 0 ? sp : itemData.subtotal;
      }
    }

    grandTotal += Number(shippingValue || 0);
    practicedTotal += Number(shippingValue || 0);

    return {
      calculatedTotalQuantity: totalQuantity,
      calculatedTaxes: totalTaxes,
      calculatedGrandTotal: grandTotal,
      calculatedPracticedTotal: practicedTotal,
    };
  }, [items, shippingValue]);

  useEffect(() => {
    setValue('totalQuantity', totals.calculatedTotalQuantity, { shouldValidate: false });
    setValue('taxes', totals.calculatedTaxes, { shouldValidate: false });
    setValue('total', totals.calculatedPracticedTotal, { shouldValidate: false });
  }, [totals, setValue]);

  return {
    totalQuantity: totals.calculatedTotalQuantity,
    taxes: totals.calculatedTaxes,
    total: totals.calculatedGrandTotal,
    practicedTotal: totals.calculatedPracticedTotal,
  };
};